// WorkLogPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listWorkLogs, startWorkLog, stopWorkLog, updateWorkLog, payWorkLog, setWorkLogWidgetOpen } from "../../actions/workLogActions";
import WorkLogTimerWidget from "./WorkLogTimerWidget";
import { MainContainer } from "../MainContainer";

const fmt2 = (n) => String(n).padStart(2, "0");

const WorkLogPage = ({ defaultOrderId, defaultEstimateId, defaultPerformerId }) => {
    const dispatch = useDispatch();
    const { items, loading, creating, totalMinutes, totalAmount, error, running, widgetOpen } = useSelector((s) => s.workLog);

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [hourlyRate, setHourlyRate] = useState(1000);
    const [roundTo, setRoundTo] = useState(15);
    const [localError, setLocalError] = useState("");

    const [orderId, setOrderId] = useState(defaultOrderId || "");
    const [estimateId, setEstimateId] = useState(defaultEstimateId || "");
    const [performerId, setPerformerId] = useState(defaultPerformerId || "");
    const [customerId, setCustomerId] = useState("");

    const [editId, setEditId] = useState(null);
    const [editDraft, setEditDraft] = useState({});

    useEffect(() => {
        const params = {};
        if (defaultOrderId) params.order = defaultOrderId;
        if (defaultEstimateId) params.estimate = defaultEstimateId;
        dispatch(listWorkLogs(params));
    }, [dispatch, defaultOrderId, defaultEstimateId]);

    const totalHours = (totalMinutes / 60).toFixed(1);

    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!running?.startedAt) return;
        const t = setInterval(() => setTick((x) => x + 1), 1000);
        return () => clearInterval(t);
    }, [running?.startedAt]);

    const elapsed = useMemo(() => {
        if (!running?.startedAt) return null;
        const start = new Date(running.startedAt).getTime();
        const now = Date.now();
        const diff = Math.max(0, now - start);
        const totalSec = Math.floor(diff / 1000);
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;
        return `${fmt2(h)}:${fmt2(m)}:${fmt2(s)}`;
    }, [running, tick]);

    const authUserId =

        useSelector((s) =>
            s?.auth?.user?._id ||
            s?.auth?.userInfo?._id ||
            s?.userLogin?.userInfo?._id ||
            s?.userSignin?.user?._id ||
            ""
        );

    useEffect(() => {
        const resolved = defaultPerformerId || authUserId;
        if (!performerId && resolved) setPerformerId(resolved);
    }, [defaultPerformerId, authUserId]);
    const handleStart = async () => {
        setLocalError("");

        if (!title.trim()) return;

        const performerResolved = performerId || defaultPerformerId || authUserId;
        if (!performerResolved) {
            setLocalError("Не указан исполнитель (performer). Нет userId и не передан defaultPerformerId.");
            return;
        }

        const payload = {
            title: title.trim(),
            description: description.trim(),
            date,
            hourlyRate: Number(hourlyRate) || 1000,
            roundTo: Number(roundTo) || 15,
            performer: performerResolved,
        };

        if (orderId) payload.order = orderId;
        if (estimateId) payload.estimate = estimateId;
        if (customerId) payload.customer = customerId;

        await dispatch(startWorkLog(payload));
    };


    const handleStop = async () => {
        if (!running?._id) return;
        await dispatch(stopWorkLog(running._id));
    };

    const openEdit = (item) => {
        setEditId(item._id);
        setEditDraft({
            title: item.title || "",
            description: item.description || "",
            minutes: item.minutes || 0,
            hourlyRate: item.hourlyRate || 1000,
            paymentStatus: item.paymentStatus || "created",
            paidAmount: item.paidAmount || 0,
        });
    };

    const saveEdit = async (id) => {
        const payload = {
            title: editDraft.title,
            description: editDraft.description,
            minutes: Number(editDraft.minutes) || 0,
            hourlyRate: Number(editDraft.hourlyRate) || 0,
            paymentStatus: editDraft.paymentStatus,
        };

        await dispatch(updateWorkLog(id, payload));
        await dispatch(payWorkLog(id, { paidAmount: Number(editDraft.paidAmount) || 0, paymentStatus: editDraft.paymentStatus }));
        setEditId(null);
        setEditDraft({});
    };

    return (
        <MainContainer
            noneCreateButton
            backButtonProduct
            type="minimalFunctional"
            backHref
            title="Тайминг"
        >
            <div className="work-log">
                {widgetOpen && <WorkLogTimerWidget />}

                <div className="work-log__header">
                    <div className="work-log__header-top">
                        <h1 className="work-log__title">Учёт проектных работ</h1>

                        <div className="work-log__header-actions">
                            <button
                                className={`work-log__btn ${widgetOpen ? "is-active" : ""}`}
                                type="button"
                                onClick={() => dispatch(setWorkLogWidgetOpen(!widgetOpen))}
                            >
                                Виджет
                            </button>
                        </div>
                    </div>

                    <div className="work-log__summary">
                        <div className="work-log__summary-item">
                            <span className="work-log__summary-label">Итого времени:</span>
                            <span className="work-log__summary-value">{totalMinutes} мин ({totalHours} ч)</span>
                        </div>
                        <div className="work-log__summary-item">
                            <span className="work-log__summary-label">Итого сумма:</span>
                            <span className="work-log__summary-value">{totalAmount.toLocaleString("ru-RU")} ₽</span>
                        </div>
                        {running && (
                            <div className="work-log__summary-item">
                                <span className="work-log__summary-label">Таймер:</span>
                                <span className="work-log__summary-value">{elapsed || "00:00:00"}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="work-log__body">
                    <div className="work-log__form">
                        <div className="work-log__row">
                            <label className="work-log__label">Название работы</label>
                            <input className="work-log__input" value={title} onChange={(e) => setTitle(e.target.value)} />
                        </div>

                        <div className="work-log__row work-log__row--split">
                            <div className="work-log__col">
                                <label className="work-log__label">Дата</label>
                                <input type="date" className="work-log__input" value={date} onChange={(e) => setDate(e.target.value)} />
                            </div>

                            <div className="work-log__col">
                                <label className="work-log__label">₽ / час</label>
                                <input type="number" className="work-log__input" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} />
                            </div>

                            <div className="work-log__col">
                                <label className="work-log__label">Округление, мин</label>
                                <input type="number" className="work-log__input" value={roundTo} onChange={(e) => setRoundTo(e.target.value)} />
                            </div>
                        </div>

                        <div className="work-log__row">
                            <label className="work-log__label">Описание</label>
                            <textarea className="work-log__textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
                        </div>

                        {(localError || error) && <div className="work-log__error">{localError || error}</div>}

                        <div className="work-log__actions">
                            {!running ? (
                                <button className="work-log__submit" type="button" disabled={creating} onClick={handleStart}>
                                    {creating ? "…" : "Start"}
                                </button>
                            ) : (
                                <button className="work-log__submit work-log__submit--danger" type="button" disabled={creating} onClick={handleStop}>
                                    {creating ? "…" : "Stop"}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="work-log__list">
                        {loading && <div className="work-log__list-info">Загрузка…</div>}
                        {!loading && items.length === 0 && <div className="work-log__list-info">Записей нет</div>}

                        {items.map((item) => {
                            const isEdit = editId === item._id;

                            return (
                                <div key={item._id} className={`work-log__item ${item.status === "running" ? "is-running" : ""}`}>
                                    <div className="work-log__item-header">
                                        <div className="work-log__item-title">{item.title}</div>
                                        <div className="work-log__item-amount">{(item.amount || 0).toLocaleString("ru-RU")} ₽</div>
                                    </div>

                                    <div className="work-log__item-meta">
                                        <span>{item.minutes || 0} мин · {(item.hourlyRate || 0).toLocaleString("ru-RU")} ₽/ч</span>
                                        <span className={`work-log__badge is-${item.paymentStatus || "created"}`}>{item.paymentStatus || "created"}</span>
                                        <span>оплачено: {(item.paidAmount || 0).toLocaleString("ru-RU")} ₽</span>
                                    </div>

                                    {item.description && !isEdit && <div className="work-log__item-description">{item.description}</div>}

                                    {isEdit && (
                                        <div className="work-log__edit">
                                            <div className="work-log__row work-log__row--split">
                                                <div className="work-log__col">
                                                    <label className="work-log__label">Название</label>
                                                    <input className="work-log__input" value={editDraft.title} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} />
                                                </div>
                                                <div className="work-log__col">
                                                    <label className="work-log__label">Минуты</label>
                                                    <input className="work-log__input" type="number" value={editDraft.minutes} onChange={(e) => setEditDraft({ ...editDraft, minutes: e.target.value })} />
                                                </div>
                                                <div className="work-log__col">
                                                    <label className="work-log__label">₽/час</label>
                                                    <input className="work-log__input" type="number" value={editDraft.hourlyRate} onChange={(e) => setEditDraft({ ...editDraft, hourlyRate: e.target.value })} />
                                                </div>
                                            </div>

                                            <div className="work-log__row">
                                                <label className="work-log__label">Описание</label>
                                                <textarea className="work-log__textarea" value={editDraft.description} onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })} />
                                            </div>

                                            <div className="work-log__row work-log__row--split">
                                                <div className="work-log__col">
                                                    <label className="work-log__label">Статус</label>
                                                    <select className="work-log__input" value={editDraft.paymentStatus} onChange={(e) => setEditDraft({ ...editDraft, paymentStatus: e.target.value })}>
                                                        <option value="created">created</option>
                                                        <option value="in_work">in_work</option>
                                                        <option value="to_pay">to_pay</option>
                                                        <option value="partial">partial</option>
                                                        <option value="paid">paid</option>
                                                    </select>
                                                </div>
                                                <div className="work-log__col">
                                                    <label className="work-log__label">Оплачено, ₽</label>
                                                    <input className="work-log__input" type="number" value={editDraft.paidAmount} onChange={(e) => setEditDraft({ ...editDraft, paidAmount: e.target.value })} />
                                                </div>
                                                <div className="work-log__col">
                                                    <label className="work-log__label">Действия</label>
                                                    <div className="work-log__edit-actions">
                                                        <button className="work-log__btn" type="button" onClick={() => saveEdit(item._id)}>Save</button>
                                                        <button className="work-log__btn is-secondary" type="button" onClick={() => setEditId(null)}>Cancel</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {!isEdit && item.status !== "running" && (
                                        <div className="work-log__item-actions">
                                            <button className="work-log__btn" type="button" onClick={() => openEdit(item)}>Edit</button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </MainContainer>
    );
};

export default WorkLogPage;
